import { InputAdornment, TextField } from "@mui/material";
import { isEqual } from "lodash-es";
import React, { useMemo, useState } from "react";

import Field from "./Field";

export default function MultiValuesField({
  input,
  name,
  label,
  values,
  defaultValues = [input.type.defaultValue === undefined ? null : input.type.defaultValue],
  onChange,
  ...others
}) {
  const controled = values !== undefined;
  const controledInitial = useMemo(() => controled, []);
  if (controled && !controledInitial)
    throw new Error(`Cannot change '${name || label}' from uncontrolled to controlled.`);
  if (!controled && controledInitial)
    throw new Error(`Cannot change '${name || label}' from controlled to uncontrolled.`);

  if (!controled)
    return (
      <UncontrolledMultiValuesField
        input={input}
        name={name}
        label={label}
        defaultValues={defaultValues}
        onChange={onChange}
        {...others}
      />
    );

  return (
    <ControlledMultiValuesField
      input={input}
      name={name}
      label={label}
      values={values}
      onChange={onChange}
      {...others}
    />
  );
}

function ControlledMultiValuesField({ values, onChange, ...others }) {
  const { input, label, helperText, disabled } = others;

  // eliminate unexpected undefined
  const inputDefaultValue = input.type.defaultValue === undefined ? null : input.type.defaultValue;
  values = values.map((value) => (value === undefined ? inputDefaultValue : value));

  const indeterminate = !values.length || values.some((value) => !isEqual(value, values[0]));

  if (indeterminate)
    return (
      <TextField
        label={label}
        helperText={helperText}
        value="Multiple values"
        InputProps={{
          disabled: true,
          endAdornment: !disabled && (
            <InputAdornment position="end" style={{ gap: 10 }}>
              <a href="#" onClick={() => onChange?.(values.map(() => inputDefaultValue))}>
                reset value
              </a>
            </InputAdornment>
          ),
        }}
      />
    );

  return (
    <Field
      //
      value={values[0]}
      onChange={(valueNew) => onChange?.(values.map(() => valueNew))}
      {...others}
    />
  );
}

function UncontrolledMultiValuesField({ defaultValues, onChange, ...others }) {
  const [values, valuesSet] = useState(defaultValues);
  const { input } = others;

  // eliminate unexpected undefined
  const inputDefaultValue = input.type.defaultValue === undefined ? null : input.type.defaultValue;
  defaultValues = defaultValues.map((value) => (value === undefined ? inputDefaultValue : value));

  return (
    <ControlledMultiValuesField
      values={values}
      onChange={(valuesNew) => {
        valuesSet(valuesNew);
        onChange?.(valuesNew);
      }}
      {...others}
    />
  );
}
